import {
  Action,
  ArticleId,
  currencyConverterAppDefID,
  Events,
  FedopsInteractions as EcomFedopsInteractions,
  SPECS,
} from '../../editor-script/constants';
import {PageMap} from '@wix/wixstores-client-core/dist/src/constants';
import {getAppVersion} from '@wix/wixstores-client-core/dist/src/app-version/appVersion';
import {Experiments} from '../experiments/Experiments';
import {AppManifest, AppManifestBuilder} from '@wix/app-manifest-builder';
import {EditorSDK, ContextParams} from '@wix/platform-editor-sdk';
import {
  configureCategoryGfpp,
  configureGridGalleryGfpp,
  configureSideCartLightboxGfpp,
  configureStorePageGfpp,
} from './configureGridGalleryGfpp';
import {
  FedopsInteractions as StoresFedopsInteractions,
  AppReflowPageVariantID,
  AppReflowPremiumFeatureName,
} from '../../stores-editor-script/constants';
import {configureSlots} from './configureSlots';
import {configureMembersWidgets} from './configureMembersWidgets';
import {WIX_NEW_STORES} from '@wix/app-definition-ids';
import {getShopPagesTpaPageId, hasCategoryPageOnSite} from '../../stores-editor-script/utils/pages';
import {StoresWidgetID} from '@wix/wixstores-client-core';
import {GalleryWidgetViewState} from '@wix/wixstores-client-storefront-sdk/dist/src/enums/galleryWidgetViewState';

let experiments;
let isEditor3 = false;

export function getEcomPagesConfiguration(t, version, locale) {
  return {
    [PageMap.CART]: {
      actions: [],
      settings: [
        {
          helpId: ArticleId.storesPages,
          type: 'page_info',
          title: t('Pages_Panel_pageInfo'),
          url: `https://static.parastorage.com/services/wixstores-client-worker/${version}/info-pages/cart_${locale}.html`,
        },
        {
          type: 'layout',
        },
        {
          type: 'permissions',
        },
        ...(isEditor3
          ? [
              {
                title: t('Pages_Panel_SEO_Accessibility'),
                type: 'a11y',
              },
            ]
          : []),
      ],
      descriptors: {
        icon: 'storesPageType',
        orderIndex: 1,
        tooltip: t('Pages_Store_Cart_Info_WhatisText'),
      },
    },
    [PageMap.THANKYOU]: {
      actions: [],
      settings: [
        {
          helpId: ArticleId.storesPages,
          type: 'page_info',
          title: t('Pages_Panel_pageInfo'),
          url: `https://static.parastorage.com/services/wixstores-client-worker/${version}/info-pages/thankYouPage_${locale}.html`,
        },
        {
          type: 'layout',
        },
        {
          type: 'permissions',
        },
        ...(isEditor3
          ? [
              {
                title: t('Pages_Panel_SEO_Accessibility'),
                type: 'a11y',
              },
            ]
          : []),
      ],
      descriptors: {
        icon: 'storesPageType',
        orderIndex: 0,
        tooltip: t('Pages_Store_Thankyou_Info_WhatisText'),
      },
    },
  };
}

function getApplicationActions(isStores, isCategorySite, hasCategoryPage, hasBlocksProductPage, t) {
  const manageStoresAction = {
    title: t('Pages_Panel_manageCTA'),
    event: Events.manageStores,
    icon: 'settingsAction',
  };
  const addShopPage = {
    title: t('NewPages_Panel_StorePages_Panel_AddShopPage_General'),
    event: Events.addShopPage,
    icon: 'addPagePagesPanel',
    type: 'add_page',
    tooltip: t('PagesPanel_StorePages_AddShopPage_MainButton_Tooltip'),
  };
  const addCategoryPage = {
    title: t('pagesPanel.stores.settings.categoryPage.optIn.button'),
    event: Events.addCategoryPage,
    icon: 'page_dynamic_icon',
    type: 'add_page',
  };
  const addBlocksProductPage = {
    title: 'Get new Product Page',
    event: Events.addBlocksProductPage,
    icon: 'page_dynamic_icon',
    type: 'add_page',
  };
  const deleteApp = {
    title: t('pagesPanel.stores.settings.deleteStore.button'),
    event: Events.deleteApp,
    icon: 'deleteAction',
    type: 'app_remove',
  };
  const applicationActions = {
    default: {
      defaultValues: [],
    },
  };
  if (isStores) {
    const defaultValues = [manageStoresAction];
    if (experiments.enabled(SPECS.ShowAddCategoryPageEntryPoint) && !hasCategoryPage) {
      defaultValues.push(addShopPage, addCategoryPage);
    } else if (!isCategorySite) {
      defaultValues.push(addShopPage);
    }

    if (experiments.enabled(SPECS.ShowAddBlocksProductPageEntryPoint) && !hasBlocksProductPage) {
      defaultValues.push(addBlocksProductPage);
    }

    defaultValues.push(deleteApp);
    applicationActions.default.defaultValues = defaultValues;
  }

  return applicationActions;
}

function getStoresAppReflowConfiguration(t, version) {
  const getAssetsUrl = () => `https://static.parastorage.com/services/wixstores-client-worker/${version}/assets/images`;
  const reflowConfiguration = {};

  const productPageVariants = [
    {
      id: AppReflowPageVariantID.DuplicateOriginalProductPage,
      name: t('newProductPage.createModal.optionDefault.title'),
      src: `${getAssetsUrl()}/app-reflow-default-product-page.png`,
      description: t('newProductPage.createModal.optionDefault.subtitle'),
    },
    {
      id: AppReflowPageVariantID.StartFromBlankProductPage,
      name: t('newProductPage.createModal.optionCode.title'),
      src: `${getAssetsUrl()}/app-reflow-blank-product-page.png`,
      description: t('newProductPage.createModal.optionCode.subtitle'),
    },
  ];

  reflowConfiguration[PageMap.PRODUCT] = {
    replaceable: false,
    variantsData: {
      variants: productPageVariants,
      variantsPanelOptions: {
        customSubtitle: {
          text: t('newProductPage.createModal.body.text'),
          learnMoreUrl: t('newProductPage.createModal.body.kbURL'),
        },
        showFooter: false,
        hideAddedVariants: false,
      },
    },
    premiumFeatureName: AppReflowPremiumFeatureName.ProductPage,
  };

  const categoryPageVariants = [
    {
      id: AppReflowPageVariantID.DuplicateOriginalCategoryPage,
      name: t('newProductPage.createModal.optionDefault.title'),
      src: `${getAssetsUrl()}/app-reflow-default-category-page.png`,
      description: t('pagesPanel.addCategoryPageVariant.modal.optionDefault.title'),
    },
    {
      id: AppReflowPageVariantID.StartFromBlankCategoryPage,
      name: t('newProductPage.createModal.optionCode.title'),
      src: `${getAssetsUrl()}/app-reflow-blank-product-page.png`,
      description: t('pagesPanel.addCategoryPageVariant.modal.optionCode.subtitle'),
    },
  ];

  reflowConfiguration[PageMap.CATEGORY] = {
    replaceable: false,
    variantsData: {
      variants: categoryPageVariants,
      variantsPanelOptions: {
        customSubtitle: {
          text: t('pagesPanel.addCategoryPageVariant.modal.body'),
          learnMoreUrl: t('pagesPanel.addCategoryPageVariant.modal.kbURL'),
        },
        showFooter: false,
        hideAddedVariants: false,
      },
    },
    premiumFeatureName: AppReflowPremiumFeatureName.CategoryPage,
  };

  return reflowConfiguration;
}

function getMyBusinessPanelConfiguration(
  locale: string,
  hasStoresPremium: boolean,
  t: (key: string) => string,
  assetsUrl: string
) {
  let myBusinessMainActions;

  if (isEditor3) {
    myBusinessMainActions = [
      {
        title: t('App_Manager_Stores_Main_CTA'),
        actionId: Action.openManageProducts,
        icon: 'tag',
      },
      {
        title: t('businessPanel.stores.mainActions.categories.cta'),
        actionId: Action.openManageCategories,
        icon: 'collections',
      },
      {
        title: t('businessPanel.stores.mainActions.orders.cta'),
        actionId: Action.openOrders,
        icon: 'order',
      },
      {
        title: t('businessPanel.stores.mainActions.shipping.cta'),
        actionId: Action.editShipping,
        icon: 'shipping',
      },
    ];
  } else {
    myBusinessMainActions = [
      {
        title: t('App_Manager_Stores_Main_CTA'),
        actionId: Action.openManageProducts,
        icon: 'variations',
      },
      {
        title: t('App_Manager_Stores_Secondary_CTA'),
        actionId: Action.openDashboard,
        icon: 'appManager_settingsAction',
      },
    ];
  }

  let myBusinessCustomActions;

  if (isEditor3) {
    myBusinessCustomActions = [
      {
        title: t('businessPanel.stores.moreActions.inventory.cta'),
        actionId: Action.openInventory,
        icon: 'bulletListThin',
      },
      {
        title: t('businessPanel.stores.moreActions.checkout.cta'),
        actionId: Action.editCheckoutPage,
        icon: 'cart',
      },
      {
        title: t('businessPanel.stores.moreActions.paymentSettings.cta'),
        actionId: Action.gettingPaidSettings,
        icon: 'payment',
      },
      {
        title: t('businessPanel.stores.moreActions.taxSettings.cta'),
        actionId: Action.openTaxSettings,
        icon: 'tax',
      },
      {
        title: t('businessPanel.stores.moreActions.storeSettings.cta'),
        actionId: Action.openStoreSettings,
        icon: 'appManager_settingsAction',
      },
    ];
  } else {
    myBusinessCustomActions = [
      {
        title: t('App_Manager_Stores_First_Quick_Action'),
        actionId: Action.openAddPanel,
        icon: 'appManager_addElementsAction',
        type: 'editorActions',
      },
      {
        title: t('App_Manager_Stores_Second_Quick_Action'),
        actionId: Action.openPagesPanel,
        icon: 'appManager_pagesAction',
        type: 'editorActions',
      },
      // {
      //   title: t('App_Manager_Stores_Fourth_Quick_Action'),
      //   actionId: Action.addRelatedApps,
      //   icon: 'appManager_relatedAppsAction',
      //   type: 'editorActions',
      // },
    ];

    if (locale === 'en') {
      myBusinessCustomActions.splice(2, 0, {
        title: t('App_Manager_Stores_Third_Quick_Action'),
        actionId: Action.findProductsToSell,
        icon: 'market',
        type: 'editorActions',
      });
    }
  }

  const panelConfiguration = {
    mainActions: myBusinessMainActions,
    customActions: myBusinessCustomActions,
    defaultActions: {
      upgrade: hasStoresPremium
        ? {}
        : {
            upgradeType: 'SITE_UPGRADE',
            upgradeText: isEditor3 ? t('businessPanel.stores.upgrade.text') : t('App_Manager_Stores_Upgrade_Text'),
            upgradeLinkText: isEditor3 ? t('businessPanel.stores.upgrade.cta') : t('App_Manager_Stores_Upgrade_Link'),
          },
      learnMoreKB: ArticleId.stores,
    },
  };

  if (isEditor3) {
    (panelConfiguration as any).application = {
      name: t('businessPanel.stores.header.title'),
      editorSetup: {
        title: t('businessPanel.stores.startSelling.title'),
        description: t('businessPanel.stores.startSelling.subttile'),
      },
      pages: {
        images: [`${assetsUrl}/stores-2 color.jpg`, `${assetsUrl}/stores-1.jpg`],
      },
    };
  }

  return panelConfiguration;
}

export async function getAppManifest({
  t,
  locale,
  token,
  hasStoresPremium,
  exps,
  appManifestBuilder,
  editorSDK,
  options,
  fedopsLogger,
  pagesPanelTitle,
  extraParams,
}: {
  t: (key: string) => string;
  locale: string;
  token: string;
  hasStoresPremium: boolean;
  exps: Experiments;
  appManifestBuilder: AppManifestBuilder;
  editorSDK: EditorSDK;
  options: ContextParams;
  fedopsLogger: Record<string, any>;
  pagesPanelTitle: string;
  extraParams?: {
    isCategorySite?: boolean;
    hasBlocksProductPage?: boolean;
    numOfProductPageVariants?: number;
    numOfCategoryPageVariants?: number;
  };
}) {
  const isStores = options.initialAppData.appDefinitionId === WIX_NEW_STORES;
  const interactionType = isStores
    ? StoresFedopsInteractions.StoresAppManifestCompletion
    : EcomFedopsInteractions.EcomAppManifestCompletion;
  try {
    experiments = exps || new Experiments();
    isEditor3 = (options.origin.subType as any) === 'EDITOR3';

    const version = getAppVersion();
    const assetsUrl = `https://static.parastorage.com/services/wixstores-client-worker/${version}/assets/images`;
    const ecomPagesConfiguration = getEcomPagesConfiguration(t, version, locale);
    const isCategorySite = extraParams?.isCategorySite;
    const shopPagesTpaId = (await getShopPagesTpaPageId(editorSDK)) || [];
    const numOfShopPages = shopPagesTpaId.length;
    const hasCategoryPage = await hasCategoryPageOnSite(editorSDK);
    const hasBlocksProductPage = extraParams?.hasBlocksProductPage;

    fedopsLogger?.interactionStarted?.(interactionType);

    const defaultPageActions =
      isCategorySite && numOfShopPages < 2 && !experiments.enabled(SPECS.AllowRemovingLastShopWhenCategoryExists)
        ? []
        : [
            'Pages_Actions_Page_Rename',
            {
              title: t('Pages_Panel_ShopPage_Settings_Delete'),
              event: Events.deleteShopPage,
              icon: 'deleteAction',
              type: 'page_remove',
            },
          ];
    const productPageVariantActions = [
      {
        title: t('pagesPanel.productPage.moreActions.manageDisplayedProducts'),
        event: Events.editProductPageVariantRule,
        icon: 'variations',
        type: 'editorActions',
      },
      {
        title: t('pagesPanel.productPage.moreActions.duplicateProductPage'),
        event: Events.duplicateProductPageVariant,
        icon: 'variations',
        type: 'editorActions',
        premiumFeatureName: AppReflowPremiumFeatureName.ProductPage,
      },
    ];
    const categoryPageVariantActions = [
      {
        title: t('pagesPanel.productPage.moreActions.manageDisplayedProducts'),
        event: Events.editCategoryPageVariantRule,
        icon: 'variations',
        type: 'editorActions',
      },
      {
        title: t('pagesPanel.productPage.moreActions.duplicateProductPage'),
        event: Events.duplicateCategoryPageVariant,
        icon: 'variations',
        type: 'editorActions',
        premiumFeatureName: AppReflowPremiumFeatureName.CategoryPage,
      },
    ];

    const pageActions = {
      default: defaultPageActions,
      [PageMap.CATEGORY]: [],
      [PageMap.CATEGORY_PAGE_VARIANT]: categoryPageVariantActions,
      [PageMap.PRODUCT]: [],
      [PageMap.PRODUCT_PAGE_VARIANT]: productPageVariantActions,
      [PageMap.CART]: ecomPagesConfiguration[PageMap.CART].actions,
      [PageMap.THANKYOU]: ecomPagesConfiguration[PageMap.THANKYOU].actions,
    };

    const categoryPageSettings = [
      {
        helpId: ArticleId.storesPagesForCategorySite,
        type: 'page_info',
        title: t('Pages_Panel_pageInfo'),
        url: `https://static.parastorage.com/services/wixstores-client-worker/${version}/info-pages/categoryPage_${locale}.html?token=${token}`,
      },
      {
        type: 'layout',
      },
      {
        type: 'permissions',
      },
      ...(isEditor3
        ? [
            {
              title: t('Pages_Panel_SEO'),
              type: 'seo',
            },
            {
              title: t('Pages_Panel_SEO_Accessibility'),
              type: 'a11y',
            },
            {
              title: t('Pages_Panel_Social_Share'),
              type: 'social_share',
            },
          ]
        : []),
    ];

    const shopPageSettings = [
      {
        helpId: ArticleId.storesPages,
        type: 'page_info',
      },
      {
        type: 'layout',
      },
      {
        type: 'permissions',
      },
      {
        title: t('Pages_Panel_SEO'),
        type: 'seo',
      },
    ];

    const productPageSettings = [
      {
        helpId: isCategorySite ? ArticleId.storesPagesForCategorySite : ArticleId.storesPages,
        type: 'page_info',
        title: t('Pages_Panel_pageInfo'),
        url: `https://static.parastorage.com/services/wixstores-client-worker/${version}/info-pages/productPage_${locale}.html?token=${token}`,
      },
      {
        type: 'layout',
      },
      {
        type: 'permissions',
      },
      ...(isEditor3
        ? [
            {
              title: t('Pages_Panel_SEO'),
              type: 'seo',
            },
            {
              title: t('Pages_Panel_SEO_Accessibility'),
              type: 'a11y',
            },
            {
              title: t('Pages_Panel_Social_Share'),
              type: 'social_share',
            },
          ]
        : []),
    ];

    const pageSettings = {
      [PageMap.CATEGORY]: categoryPageSettings,
      [PageMap.CATEGORY_PAGE_VARIANT]: categoryPageSettings,
      [PageMap.GALLERY]: shopPageSettings,
      [PageMap.PRODUCT]: productPageSettings,
      [PageMap.PRODUCT_PAGE_VARIANT]: productPageSettings,
      [PageMap.CART]: ecomPagesConfiguration[PageMap.CART].settings,
      [PageMap.THANKYOU]: ecomPagesConfiguration[PageMap.THANKYOU].settings,
    };

    for (const shopPageId of shopPagesTpaId) {
      pageSettings[shopPageId] = shopPageSettings;
    }

    const applicationSettings = {
      default: {
        displayName: pagesPanelTitle,
        helpId: isCategorySite ? ArticleId.storesPagesForCategorySite : ArticleId.storesPages,
        pageReplace: getStoresAppReflowConfiguration(t, version),
      },
    };

    const applicationActions = getApplicationActions(
      isStores,
      isCategorySite,
      hasCategoryPage,
      hasBlocksProductPage,
      t
    );

    const shopPageDescriptors = {
      icon: 'storesPageType',
      orderIndex: 2,
    };
    const pageDescriptors = {
      [PageMap.CATEGORY]: {
        icon: 'storesPageType',
        orderIndex: 2,
        tooltip:
          extraParams?.numOfCategoryPageVariants === 0
            ? t('pages.store.categoryPage.hoverTooltip')
            : t('pagesPanel.stores.categoryPageDefault.tooltip'),
      },
      [PageMap.CATEGORY_PAGE_VARIANT]: {
        icon: 'storesPageType',
        tooltip: t('pagesPanel.stores.categoryPageVariant.tooltip'),
      },
      [PageMap.GALLERY]: shopPageDescriptors,
      [PageMap.PRODUCT]: {
        icon: 'storesPageType',
        orderIndex: 3,
        tooltip:
          extraParams?.numOfProductPageVariants === 0
            ? t('pagesPanel.stores.productPage.tooltip')
            : t('pagesPanel.stores.ProductPageDefault.tooltip'),
      },
      [PageMap.PRODUCT_PAGE_VARIANT]: {
        icon: 'storesPageType',
        tooltip: t('pagesPanel.stores.productPageVariant.tooltip'),
      },
      [PageMap.CART]: ecomPagesConfiguration[PageMap.CART].descriptors,
      [PageMap.THANKYOU]: ecomPagesConfiguration[PageMap.THANKYOU].descriptors,
    };
    for (const shopPageId of shopPagesTpaId) {
      pageDescriptors[shopPageId] = shopPageDescriptors;
    }

    const appManifest = buildAppManifest({t, editorSDK, appManifestBuilder, options});

    appManifest.pages = {
      pageActions,
      pageSettings,
      applicationSettings,
      applicationActions,
      pageDescriptors,
      sortInnerRoutes: 'no-sort',
    } as unknown as AppManifest['pages'];

    appManifest.appDescriptor = getMyBusinessPanelConfiguration(
      locale,
      hasStoresPremium,
      t,
      assetsUrl
    ) as unknown as AppManifest['appDescriptor'];

    fedopsLogger?.interactionEnded?.(interactionType);

    return appManifest;
  } catch (err) {
    fedopsLogger?.interactionFailed?.(interactionType, err as Error);
  }
}

function buildAppManifest({
  t,
  editorSDK,
  appManifestBuilder,
  options,
}: {
  t: (key: string) => string;
  editorSDK: EditorSDK;
  appManifestBuilder: AppManifestBuilder;
  options: ContextParams;
}): AppManifest {
  const isClassicEditor = options.origin.type === 'CLASSIC';
  const isEditorX = options.origin.type === 'RESPONSIVE';
  const hidePluginGfpp =
    (widgetId: StoresWidgetID) =>
    (appManifestBuilder2: AppManifestBuilder): AppManifestBuilder =>
      appManifestBuilder2.configureWidget(widgetId, (widgetBuilder) => {
        widgetBuilder.gfpp().set('widgetPlugins', {behavior: 'HIDE'});
        widgetBuilder.behavior().set({removable: false});
      });
  const galleryWidgetViewStatesConfigurator = (widgetIds: StoresWidgetID[]) => {
    widgetIds.forEach((widgetId) => {
      appManifestBuilder.configureWidget(widgetId, (widgetBuilder) => {
        widgetBuilder
          .hideAllWidgetViewStates()
          .configureWidgetViewState(GalleryWidgetViewState.DEFAULT, (widgetViewStateBuilder) => {
            widgetViewStateBuilder.title(GalleryWidgetViewState.DEFAULT).useAsInitialViewState();
          })
          .configureWidgetViewState(GalleryWidgetViewState.QUICK_ADD, (widgetViewStateBuilder) => {
            widgetViewStateBuilder.title(GalleryWidgetViewState.QUICK_ADD);
          });
      });
    });
  };

  const configFunctions =
    isClassicEditor && experiments.enabled(SPECS.GalleryNewGFPP)
      ? [
          configureGridGalleryGfpp({t, editorSDK}),
          configureStorePageGfpp({t, editorSDK}),
          configureCategoryGfpp({t, editorSDK}),
        ]
      : [];

  if ((!isEditorX && experiments.enabled(SPECS.ConfigureSlotsInEditorSDK)) || isEditorX) {
    configFunctions.push(configureSlots);
  }

  if (experiments.enabled(SPECS.ConfigureMembersWidgets)) {
    configFunctions.push(configureMembersWidgets);
  }

  if (experiments.enabled(SPECS.SearchPluginsGFPPPreventDelete)) {
    configFunctions.push(
      hidePluginGfpp(StoresWidgetID.SEARCH_RESULTS_GALLERY),
      hidePluginGfpp(StoresWidgetID.SEARCH_MODAL_GALLERY)
    );
  }

  if (experiments.enabled(SPECS.ConfigureGalleryViewStates)) {
    galleryWidgetViewStatesConfigurator([
      StoresWidgetID.CATEGORY_PAGE,
      StoresWidgetID.GRID_GALLERY,
      StoresWidgetID.SLIDER_GALLERY,
      StoresWidgetID.SEARCH_RESULTS_GALLERY,
      StoresWidgetID.SEARCH_MODAL_GALLERY,
    ]);
  }

  configFunctions.push(configureSideCartLightboxGfpp());

  return configFunctions.length ? pipe(...configFunctions)(appManifestBuilder).build() : {};
}

export function handleAppsManagerActions(sdk: EditorSDK, action: Action) {
  switch (action) {
    case Action.openDashboard:
      return sdk.editor.openDashboardPanel('', {url: '', closeOtherPanels: false});
    case Action.openManageProducts:
      return sdk.editor.openDashboardPanel('', {url: 'products', closeOtherPanels: false});
    case Action.openManageCategories:
      return sdk.editor.openDashboardPanel('', {url: 'wix-stores/categories/list', closeOtherPanels: false});
    case Action.openInventory:
      return sdk.editor.openDashboardPanel('', {url: 'wix-stores/inventory', closeOtherPanels: false});
    case Action.openOrders:
      return sdk.editor.openDashboardPanel('', {url: 'ecom-platform/orders-list', closeOtherPanels: false});
    case Action.editShipping:
      return sdk.editor.openDashboardPanel('', {url: 'ecom-platform/delivery-profile', closeOtherPanels: false});
    case Action.editCheckoutPage:
      return sdk.editor.openDashboardPanel('', {url: 'ecom-platform/checkout-settings', closeOtherPanels: false});
    case Action.gettingPaidSettings:
      return sdk.editor.openDashboardPanel('', {
        url: 'wix-invoices/settings/general-settings',
        closeOtherPanels: false,
      });
    case Action.openTaxSettings:
      return sdk.editor.openDashboardPanel('', {url: 'ecom-platform/tax', closeOtherPanels: false});
    case Action.openStoreSettings:
      return sdk.editor.openDashboardPanel('', {url: 'store/settings', closeOtherPanels: false});
    case Action.openAddPanel:
      return sdk.editor.deeplink.show('', {
        type: 'addPanel',
        params: [WIX_NEW_STORES],
      });
    case Action.openPagesPanel:
      return sdk.editor.deeplink.show('', {
        type: 'pagesPanel',
        params: [WIX_NEW_STORES],
      });
    case Action.findProductsToSell:
      return sdk.editor.openDashboardPanel('', {url: 'store/explore-products', closeOtherPanels: false});
    case Action.addRelatedApps:
      return sdk.editor.deeplink.show('', {type: 'addPanel', params: [currencyConverterAppDefID]});
  }
}

function pipe<D>(...funcs: ((param: D) => D)[]): (param: D) => D {
  return (initialUInput) => funcs.reduce((result, currentFn) => currentFn(result), initialUInput);
}
